import { useState, type ReactNode } from 'react';
import { fmtCompact, fmtDay, fmtInt, fmtK } from '../utils';

/*
 * The small vocabulary the design is built from. The design draws its charts as
 * plain div bars and SVG areas (no charting library), and so do these — it keeps
 * the rendering faithful and means bars paint without an animation frame.
 */

export function KpiTile({ label, value, sub, tone, valueTone }: { label: string; value: string; sub?: ReactNode; tone?: 'up' | 'alert'; valueTone?: 'blue' }) {
  return (
    <div className="mono-kpi">
      <div className="mono-kpi-label">{label}</div>
      <div className={`mono-kpi-value${valueTone ? ` ${valueTone}` : ''}`}>{value}</div>
      {sub !== undefined && <div className={`mono-kpi-sub${tone ? ` ${tone}` : ''}`}>{sub}</div>}
    </div>
  );
}

export function Panel({ title, action, children, tight }: { title?: string; action?: ReactNode; children: ReactNode; tight?: boolean }) {
  return (
    <section className={`mono-panel${tight ? ' tight' : ''}`}>
      {title && (
        <div className="mono-panel-head">
          <h2 className="mono-panel-title">{title}</h2>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'blue' | 'red' | 'md' }) {
  return (
    <div className="mono-stat">
      <div className="mono-stat-label">{label}</div>
      <div className={`mono-stat-value${tone ? ` ${tone}` : ''}`}>{value}</div>
      {sub && <div className="mono-stat-label">{sub}</div>}
    </div>
  );
}

/** One or two segments of a progress bar, widths in percent. */
export function Bar({
  segments,
  thick,
  thin,
  label,
}: {
  segments: Array<{ pct: number; fill: 'blue' | 'blue-dark' | 'cyan' | 'red' }>;
  thick?: boolean;
  thin?: boolean;
  label?: string;
}) {
  return (
    <div className={`mono-bar${thick ? ' thick' : ''}${thin ? ' thin' : ''}`} role="img" aria-label={label}>
      {segments.map((s, i) => (
        <span key={i} className={`fill-${s.fill}${thick && label ? ' labelled' : ''}`} style={{ width: `${Math.max(0, Math.min(100, s.pct))}%` }}>
          {thick && label && i === 0 ? label : null}
        </span>
      ))}
    </div>
  );
}

const yTicks = (max: number, steps: number) => Array.from({ length: steps + 1 }, (_, i) => fmtCompact((max * (steps - i)) / steps));

/*
 * HOVER EXPLANATIONS.
 *
 * A bar on its own says "tall"; it does not say what was counted, on which day,
 * or how the figure is defined. Every bar chart therefore shows a panel on
 * hover: the day, one labelled line per series with its colour, a total where
 * the bars are stacked or grouped, and a one-line note explaining the metric
 * (passed by the call site, since only it knows what its numbers mean).
 *
 * This replaces the browser's native `title` tooltips, which arrived after a
 * second, could not be styled, and read out a raw ISO date.
 */
type TipFill = 'blue' | 'blue-dark' | 'cyan' | 'red';
type TipRow = { label: string; value: string; fill?: TipFill; total?: boolean };

function ChartTip({
  index,
  count,
  day,
  rows,
  note,
  low,
  title,
}: {
  index: number;
  count: number;
  day: string;
  rows: TipRow[];
  note?: string;
  low?: boolean;
  /** Replaces the formatted date heading — for charts whose column is not one date. */
  title?: string;
}) {
  // Kept inside the plot area: centred over its bar, but anchored by its near
  // edge for the first and last quarter so it cannot spill out of the panel.
  const anchor = index < count / 4 ? 'start' : index > (3 * count) / 4 ? 'end' : 'center';
  return (
    <div className={`mono-tip ${anchor}${low ? ' low' : ''}`} style={{ left: `${((index + 0.5) / count) * 100}%` }} role="tooltip">
      <div className="mono-tip-day">{title ?? fmtDay(day)}</div>
      {rows.map((r) => (
        <div key={r.label} className={`mono-tip-row${r.total ? ' total' : ''}`}>
          {r.fill && <span className={`mono-swatch fill-${r.fill}`} aria-hidden="true" />}
          <span>{r.label}</span>
          <span className="mono-tip-value">{r.value}</span>
        </div>
      ))}
      {note && <div className="mono-tip-note">{note}</div>}
    </div>
  );
}

/** Which bar the pointer is on, and the handlers that track it. */
function useHoveredBar() {
  const [hovered, setHovered] = useState<number | null>(null);
  return {
    hovered,
    onBar: (i: number) => ({ onMouseEnter: () => setHovered(i), onMouseMove: () => setHovered(i) }),
    onPlot: { onMouseLeave: () => setHovered(null) },
  };
}

/**
 * Vertical bars, one per day. `highlightLast` paints the current (partial) day
 * red as the design does. `valueLabels` writes the "268k"-style row above the
 * date axis.
 */
export function Bars({
  values,
  days,
  max,
  height,
  steps = 4,
  highlightLast,
  valueLabels,
  loose,
  dayStyle = 'short',
  tone = 'blue',
  seriesLabel,
  note,
  cumulative,
  ariaLabel,
}: {
  values: number[];
  days: string[];
  max: number;
  height: number;
  steps?: number;
  highlightLast?: boolean;
  valueLabels?: boolean;
  loose?: boolean;
  dayStyle?: 'short' | 'long';
  /** The section's colour: visits are cyan in the design, everything else blue. */
  tone?: 'blue' | 'cyan';
  /** What one bar counts, e.g. "Nouveaux participants" — shown on hover. */
  seriesLabel?: string;
  /** One line explaining how the figure is defined — shown on hover. */
  note?: string;
  /** Adds the running total since day 1 to the hover card. */
  cumulative?: boolean;
  ariaLabel: string;
}) {
  const tip = useHoveredBar();
  return (
    <div className={`mono-chart${loose ? ' loose' : ''}`} role="img" aria-label={ariaLabel}>
      <div className={`mono-yaxis${loose ? ' wide' : ''}`} style={{ height }}>
        {yTicks(max, steps).map((t, i) => (
          <span key={i}>{t}</span>
        ))}
      </div>
      <div className="mono-plot">
        <div className="mono-plot-area" style={{ height }} {...tip.onPlot}>
          <div className="mono-gridlines">
            {Array.from({ length: steps + 1 }, (_, i) => (
              <div key={i} />
            ))}
          </div>
          <div className={`mono-bars${loose ? ' loose' : ''}`}>
            {values.map((v, i) => {
              const pct = max > 0 ? Math.max(0, Math.min(100, (v / max) * 100)) : 0;
              const isToday = Boolean(highlightLast) && i === values.length - 1;
              return (
                <div
                  key={i}
                  data-day={days[i]}
                  className={`bar${loose ? ' rounded' : ''}${tone === 'cyan' ? ' cyan' : ''}${isToday ? ' red' : ''}`}
                  style={{ height: `${pct}%` }}
                  {...tip.onBar(i)}
                />
              );
            })}
          </div>
          {tip.hovered !== null && (
            <ChartTip
              index={tip.hovered}
              count={values.length}
              day={days[tip.hovered]}
              low={(max > 0 ? (values[tip.hovered] / max) * 100 : 0) > 55}
              rows={[
                {
                  label: seriesLabel ?? ariaLabel,
                  value: fmtInt(values[tip.hovered]),
                  fill: highlightLast && tip.hovered === values.length - 1 ? 'red' : tone,
                },
                // Running total: the question a daily series always raises next.
                ...(cumulative
                  ? [
                      {
                        label: 'Cumul depuis le 1er jour',
                        value: fmtInt(values.slice(0, tip.hovered + 1).reduce((a, v) => a + v, 0)),
                        total: true,
                      },
                    ]
                  : []),
              ]}
              note={note}
            />
          )}
        </div>
        {valueLabels && (
          <div className={`mono-xaxis labels${loose ? ' loose' : ''}`}>
            {values.map((v, i) => (
              <span key={i}>{fmtK(v)}</span>
            ))}
          </div>
        )}
        <div className={`mono-xaxis${loose ? ' loose' : ''}`}>
          {days.map((d, i) => (
            <span key={i}>{fmtDay(d, dayStyle)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Stacked columns: bottom = physical, top = digital. */
export function StackedBars({
  values,
  days,
  max,
  height,
  labels,
  note,
  ariaLabel,
}: {
  values: Array<{ physical: number; digital: number }>;
  days: string[];
  max: number;
  height: number;
  /** What each half counts — shown on hover. */
  labels: { physical: string; digital: string; total: string };
  note?: string;
  ariaLabel: string;
}) {
  const tip = useHoveredBar();
  return (
    <div className="mono-chart loose" role="img" aria-label={ariaLabel}>
      <div className="mono-yaxis wide" style={{ height }}>
        {yTicks(max, 4).map((t, i) => (
          <span key={i}>{t}</span>
        ))}
      </div>
      <div className="mono-plot">
        <div className="mono-plot-area" style={{ height }} {...tip.onPlot}>
          <div className="mono-gridlines">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} />
            ))}
          </div>
          <div className="mono-bars loose">
            {values.map((v, i) => {
              const total = v.physical + v.digital;
              const h = max > 0 ? Math.min(100, (total / max) * 100) : 0;
              const digitalShare = total > 0 ? (v.digital / total) * 100 : 0;
              return (
                <div key={i} data-day={days[i]} className="stack" style={{ height: `${h}%` }} {...tip.onBar(i)}>
                  <div className="top" style={{ height: `${digitalShare}%` }} />
                  <div className="bottom" style={{ height: `${100 - digitalShare}%` }} />
                </div>
              );
            })}
          </div>
          {tip.hovered !== null && (
            <ChartTip
              index={tip.hovered}
              count={values.length}
              day={days[tip.hovered]}
              low={(max > 0 ? ((values[tip.hovered].physical + values[tip.hovered].digital) / max) * 100 : 0) > 55}
              rows={[
                { label: labels.physical, value: fmtInt(values[tip.hovered].physical), fill: 'blue' },
                { label: labels.digital, value: fmtInt(values[tip.hovered].digital), fill: 'cyan' },
                { label: labels.total, value: fmtInt(values[tip.hovered].physical + values[tip.hovered].digital), total: true },
              ]}
              note={note}
            />
          )}
        </div>
        <div className="mono-xaxis loose">
          {days.map((d, i) => (
            <span key={i}>{fmtDay(d, 'short')}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Cumulative rows for the grouped hover card, over paired days only. */
function runningTotals(values: Array<{ a: number; b: number | null }>, upTo: number, labels: { a: string; b: string }): TipRow[] {
  let a = 0;
  let b = 0;
  let paired = 0;
  for (let i = 0; i <= upTo; i += 1) {
    if (values[i].b === null) continue;
    a += values[i].a;
    b += values[i].b as number;
    paired += 1;
  }
  // Nothing comparable yet: a running total of 0 beside a real figure for the
  // day would read as "this year has none", which is the opposite of the truth.
  if (paired === 0) return [];
  return [
    { label: `Cumul ${labels.a.split(' ').at(-1) ?? ''}`.trim(), value: fmtInt(a), total: true },
    { label: `Cumul ${labels.b.split(' ').at(-1) ?? ''}`.trim(), value: fmtInt(b), total: true },
  ];
}

/** Two bars per day, side by side: blue then red. */
export function GroupedBars({
  values,
  days,
  max,
  height,
  labels,
  note,
  xLabels,
  tipTitles,
  missingLabel,
  cumulative,
  ariaLabel,
}: {
  /** `b` may be null: a day the second series has no figure for, drawn as a gap. */
  values: Array<{ a: number; b: number | null }>;
  days: string[];
  max: number;
  height: number;
  /** What each of the two bars counts — shown on hover. */
  labels: { a: string; b: string };
  note?: string;
  /** Overrides the date axis — e.g. campaign days, when the two bars are different dates. */
  xLabels?: string[];
  /** Overrides the tooltip heading, for the same reason. */
  tipTitles?: string[];
  /** What the tooltip says in place of a missing `b` value. */
  missingLabel?: string;
  /** Adds both running totals to the hover card, counting only paired days. */
  cumulative?: boolean;
  ariaLabel: string;
}) {
  const tip = useHoveredBar();
  return (
    <div className="mono-chart loose" role="img" aria-label={ariaLabel}>
      <div className="mono-yaxis wide" style={{ height }}>
        {yTicks(max, 4).map((t, i) => (
          <span key={i}>{t}</span>
        ))}
      </div>
      <div className="mono-plot">
        <div className="mono-plot-area" style={{ height }} {...tip.onPlot}>
          <div className="mono-gridlines">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} />
            ))}
          </div>
          <div className="mono-bars loose">
            {values.map((v, i) => (
              <div key={i} data-day={days[i]} className="group" {...tip.onBar(i)}>
                <span className="fill-blue" style={{ height: `${max > 0 ? Math.min(100, (v.a / max) * 100) : 0}%` }} />
                {v.b !== null ? (
                  <span className="fill-red" style={{ height: `${max > 0 ? Math.min(100, (v.b / max) * 100) : 0}%` }} />
                ) : (
                  <span className="gap" style={{ height: 0 }} aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
          {tip.hovered !== null && (
            <ChartTip
              index={tip.hovered}
              count={values.length}
              day={days[tip.hovered]}
              title={tipTitles?.[tip.hovered]}
              low={(max > 0 ? (Math.max(values[tip.hovered].a, values[tip.hovered].b ?? 0) / max) * 100 : 0) > 55}
              rows={[
                { label: labels.a, value: fmtInt(values[tip.hovered].a), fill: 'blue' },
                {
                  label: labels.b,
                  value: values[tip.hovered].b === null ? (missingLabel ?? '—') : fmtInt(values[tip.hovered].b as number),
                  fill: 'red',
                },
                // Running totals over the days BOTH series have a figure for,
                // so a gap in one year cannot hand the other a free lead.
                ...(cumulative ? runningTotals(values, tip.hovered, labels) : []),
              ]}
              note={note}
            />
          )}
        </div>
        <div className="mono-xaxis loose">
          {days.map((d, i) => (
            <span key={i}>{xLabels ? xLabels[i] : fmtDay(d, 'short')}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Filled line chart as an SVG path — the design's own construction. `endDot`
 * marks the latest point in red, as the hero chart does.
 */
export function AreaLine({
  values,
  days,
  max,
  height,
  color,
  fill,
  endDot,
  steps = 2,
  labels,
  ariaLabel,
}: {
  values: number[];
  days: string[];
  max: number;
  height: number;
  color: string;
  fill: string;
  endDot?: boolean;
  steps?: number;
  labels: 'ends' | 'all';
  ariaLabel: string;
}) {
  const W = 1000;
  const n = values.length;
  const pts = values.map((v, i) => {
    const x = n > 1 ? (i / (n - 1)) * W : 0;
    const y = max > 0 ? height - Math.max(0, Math.min(1, v / max)) * (height - 2) - 1 : height - 1;
    return [x, y] as const;
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${W},${height} L0,${height} Z`;
  const last = pts[pts.length - 1];
  const tickCount = steps + 1;

  return (
    <div className="mono-chart" role="img" aria-label={ariaLabel}>
      <div className="mono-yaxis" style={{ height }}>
        {yTicks(max, steps).map((t, i) => (
          <span key={i}>{t}</span>
        ))}
      </div>
      <div className="mono-plot">
        <svg className="mono-area" viewBox={`0 0 ${W} ${height}`} height={height} preserveAspectRatio="none" aria-hidden="true">
          {Array.from({ length: tickCount }, (_, i) => {
            const y = i === tickCount - 1 ? height - 1 : (i / steps) * (height - 2) + 1;
            return <line key={i} x1={0} y1={y} x2={W} y2={y} stroke={i === tickCount - 1 ? 'var(--mono-line)' : 'var(--mono-ground)'} strokeWidth={1} />;
          })}
          {n > 1 && <path d={area} fill={fill} />}
          {n > 1 && <path d={line} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />}
          {endDot && last && <circle cx={last[0]} cy={last[1]} r={4.5} fill="var(--mono-red)" vectorEffect="non-scaling-stroke" />}
        </svg>
        {labels === 'ends' ? (
          <div className="mono-xaxis ends">
            <span>{fmtDay(days[0] ?? '', 'long')}</span>
            <span>{fmtDay(days[Math.floor((n - 1) / 2)] ?? '', 'long')}</span>
            <span>{fmtDay(days[n - 1] ?? '', 'long')}</span>
          </div>
        ) : (
          <div className="mono-xaxis loose">
            {days.map((d, i) => (
              <span key={i}>{i === 0 || i === n - 1 ? fmtDay(d, 'long') : fmtDay(d, 'short')}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Label · bar · value rows (consent channels, visit sources). */
export function HBars({
  rows,
  wideLabel,
  narrowValue,
}: {
  rows: Array<{ label: string; pct: number; value: string; fill: string }>;
  wideLabel?: boolean;
  narrowValue?: boolean;
}) {
  return (
    <div className="mono-hbars">
      {rows.map((r) => (
        <div key={r.label} className="mono-hbar">
          <span className={`label${wideLabel ? ' wide' : ''}`}>{r.label}</span>
          <div className="track">
            <span className={r.fill} style={{ width: `${Math.max(0, Math.min(100, r.pct))}%` }} />
          </div>
          <span className={`value${narrowValue ? ' narrow' : ''}`}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

export function Swatch({ fill }: { fill: 'blue' | 'cyan' | 'red' | 'blue-dark' }) {
  return <span className={`mono-swatch fill-${fill}`} aria-hidden="true" />;
}
